import { useState, type FormEvent } from "react";
import type { MemberRoleName, ProductDetail } from "@shared/schemas/product";
import { Button } from "@client/components/Button";
import { ConfirmDialog } from "@client/components/ConfirmDialog";
import { SelectField, TextField } from "@client/components/Field";
import { Panel } from "@client/components/Panel";
import { useAddProductMember, useRemoveProductMember } from "@client/hooks/useProducts";
import { ApiError } from "@client/lib/api";
import { describeError } from "@client/lib/errorMessage";

const ROLE_LABEL: Record<MemberRoleName, string> = {
  OWNER: "Owner",
  EDITOR: "Editor",
  VIEWER: "Viewer",
};

/**
 * Who is attached to this product.
 *
 * Membership is about the product — its identity, its defaults, its baseline —
 * not about who may build scenarios. Anyone allow-listed can do that against any
 * product, which is the point of the tool, so this panel says as much rather
 * than letting an owner believe they are handing out access by adding people.
 */
export function ProductMembers({ product }: { product: ProductDetail }) {
  const add = useAddProductMember(product.id);
  const remove = useRemoveProductMember(product.id);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRoleName>("VIEWER");
  const [pendingRemoval, setPendingRemoval] = useState<{ userId: string; name: string } | null>(
    null,
  );

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (add.isPending) return;
    add.mutate({ email: email.trim().toLowerCase(), role }, { onSuccess: () => setEmail("") });
  }

  // The server distinguishes "that address is not on the allow list" from every
  // other refusal, and that difference is the whole point of saying it here: it
  // is an administrator's job to fix, not a typo to hunt for.
  const notAllowListed = add.error instanceof ApiError && add.error.code === "NOT_ALLOWED_LISTED";

  return (
    <Panel
      title="Members"
      description="Members can be given a say over this product's settings and baseline. Anyone on the allow list can build their own scenarios here regardless."
    >
      <ul className="flex flex-col gap-1.5">
        {product.members.map((member) => (
          <li
            key={member.userId}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-chip-bg px-3 py-2"
          >
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-xs font-semibold text-ink">
                {member.user.fullName}
                {member.userId === product.ownerId && (
                  <span className="ml-1.5 font-normal text-ink-soft">· product owner</span>
                )}
              </span>
              <span className="truncate text-[11px] text-ink-soft">{member.user.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-ink-soft">
                {ROLE_LABEL[member.role]}
              </span>
              {product.abilities.canManage && member.userId !== product.ownerId && (
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() =>
                    setPendingRemoval({ userId: member.userId, name: member.user.fullName })
                  }
                >
                  Remove
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {product.abilities.canManage && (
        <form className="flex flex-col gap-3 border-t border-line pt-3" onSubmit={onSubmit}>
          <div className="grid gap-3 sm:grid-cols-[1fr_9rem_auto] sm:items-end">
            <TextField
              label="Add by email"
              type="email"
              inputMode="email"
              autoComplete="off"
              placeholder="someone@dss.mv"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={notAllowListed ? describeError(add.error) : undefined}
            />
            <SelectField
              label="Role"
              value={role}
              onChange={(event) => setRole(event.target.value as MemberRoleName)}
            >
              <option value="VIEWER">Viewer</option>
              <option value="EDITOR">Editor</option>
              <option value="OWNER">Owner</option>
            </SelectField>
            <Button type="submit" disabled={add.isPending || email.trim().length === 0}>
              {add.isPending ? "Adding…" : "Add"}
            </Button>
          </div>

          {add.isError && !notAllowListed && (
            <p
              role="alert"
              className="text-xs font-semibold"
              style={{ color: "var(--chart-crit)" }}
            >
              {describeError(add.error)}
            </p>
          )}
        </form>
      )}

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Remove this member?"
        body={
          <>
            {pendingRemoval?.name} loses any say over this product&rsquo;s settings and baseline.
            Their scenarios are untouched, and they can still build new ones here.
          </>
        }
        confirmLabel="Remove"
        tone="danger"
        busy={remove.isPending}
        error={remove.isError ? describeError(remove.error) : null}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (!pendingRemoval) return;
          remove.mutate(pendingRemoval.userId, { onSuccess: () => setPendingRemoval(null) });
        }}
      />
    </Panel>
  );
}
